import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { useEffect, useState } from 'react'
import {
  DateField,
  Form,
  ReferenceField,
  required,
  TextInput,
  useNotify,
  useRecordContext,
  useReference,
  useRefresh,
} from 'react-admin'
import { useFormContext, useFormState } from 'react-hook-form'

import { useApi } from '../../../api/apiProvider'
import {
  INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP,
  INCIDENT_REPORT_ACTION_CONFIRM,
  INCIDENT_REPORT_ACTION_REJECT,
  INCIDENT_REPORT_ACTION_RESOLVE,
  INCIDENT_REPORT_ACTION_UPDATE_SEVERITY,
  INCIDENT_REPORT_REJECTION_REASONS,
} from '../../../config/damages'
import { ERROR_MESSAGES, getErrorDataFromError } from '../../../config/errors'

export const CONFIRM_I18N_PREFIX = 'resources.incident-reports.actions.confirm'
export const REJECT_I18N_PREFIX = 'resources.incident-reports.actions.reject'
export const CONFIRM_SECTIONS_I18N_PREFIX = `${CONFIRM_I18N_PREFIX}.sections`

export const mapChoices = (valuesMap) => Object.entries(valuesMap).map(([id, name]) => ({ id, name }))

export const sectionStyle = {
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  p: 2,
  mb: 2,
}

export const sectionTitleIconStyle = { color: 'inherit', fontSize: 18, flexShrink: 0 }

export const REJECTION_REASON_CHOICES = mapChoices(INCIDENT_REPORT_REJECTION_REASONS)
export const REJECTION_REASON_LABELS = Object.fromEntries(
  REJECTION_REASON_CHOICES.map((choice) => [choice.id, choice.name]),
)

export const ActionModalSection = ({
  titleKey,
  title,
  titleIcon: TitleIcon,
  helperKey,
  children,
  rightElement = null,
  sx = {},
}) => {
  const translate = useTranslate()
  const sectionTitle = title || (titleKey ? translate(titleKey) : '')

  return (
    <Stack
      sx={{
        ...sectionStyle,
        ...sx,
        flexDirection: { xs: 'column', md: rightElement ? 'row' : 'column' },
        alignItems: { xs: 'stretch', md: rightElement ? 'stretch' : 'stretch' },
        gap: { xs: 1.5, md: rightElement ? 2 : 0 },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1, minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'primary.main', mb: 0.25 }}>
          {TitleIcon && <TitleIcon sx={sectionTitleIconStyle} />}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 15, letterSpacing: 0, lineHeight: 1.35 }}>
            {sectionTitle}
          </Typography>
        </Box>
        {helperKey && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.75 }}>
            {translate(helperKey)}
          </Typography>
        )}
        {children}
      </Box>
      {rightElement ? (
        <Box
          sx={{
            minHeight: 0,
            flexShrink: 0,
            alignSelf: 'stretch',
            width: { xs: '100%', md: '40%' },
            minWidth: { md: 245 },
            mr: { xs: 0, md: -2 },
            mt: { xs: 0, md: -2 },
            mb: { xs: 0, md: -2 },
            ml: { xs: 0, md: 0 },
            display: 'flex',
            overflow: 'visible',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>{rightElement}</Box>
        </Box>
      ) : null}
    </Stack>
  )
}

export const ActionContextBanner = ({ vehicle }) => {
  const translate = useTranslate()

  if (!vehicle) return null

  const vehicleLine = [vehicle.brand, vehicle.model].filter(Boolean).join(' ')
  const vehicleIdentity = [vehicleLine, vehicle.designation].filter(Boolean).join(' · ')

  return (
    <Box
      sx={{
        mt: 0.45,
        width: '100%',
        px: 1.25,
        py: 0.75,
        backgroundColor: 'action.hover',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.06)',
        borderRadius: 1,
        minHeight: 42,
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        direction="row"
        spacing={0.9}
        sx={{
          color: 'text.primary',
          alignItems: 'center',
          flexWrap: 'wrap',
          width: '100%',
          justifyContent: { xs: 'flex-start', md: 'space-around' },
          columnGap: { xs: 0.9, md: 1.2 },
          rowGap: 0.35,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <DirectionsCarOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.45, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
            {vehicleIdentity || 'n/a'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.45, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.primary', mr: 0.35, lineHeight: 1.25 }}>
            {translate(`${CONFIRM_SECTIONS_I18N_PREFIX}.context.reportedBy`)}{' '}
            <ReferenceField source="reporter_id" reference="users" link={false} />
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <EventOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.45, flexShrink: 0 }} />
          <DateField
            source="created_on"
            showTime
            options={{ hour: '2-digit', minute: '2-digit', year: 'numeric', month: '2-digit', day: '2-digit' }}
            sx={{ color: 'text.primary' }}
          />
        </Box>
      </Stack>
    </Box>
  )
}

export const ActionModalHeader = ({ i18nPrefix }) => {
  const record = useRecordContext()
  const translate = useTranslate()
  const { referenceRecord: vehicle } = useReference({ reference: 'vehicles', id: record.vehicle_id })
  const dialogTitleKey = `${i18nPrefix}.dialogTitle`
  const helperTextKey = `${i18nPrefix}.helperText`
  return (
    <Box sx={{ mt: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {translate(dialogTitleKey, { _: translate(`${i18nPrefix}.label`) })}{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {record.reference}
          </Box>
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.4, mt: 0.35 }}>
        {translate(helperTextKey)}
      </Typography>
      <ActionContextBanner vehicle={vehicle} />
    </Box>
  )
}

export const IncidentReportFormDialogFooter = ({
  confirmColor = 'primary',
  confirmLabelKey,
  confirmVariant = 'contained',
  isLoading,
  onClose,
  transitionFieldName = null,
  onSubmitAction,
}) => {
  const translate = useTranslate()
  const { control, formState, handleSubmit } = useFormContext()
  const { dirtyFields } = useFormState({ control, name: transitionFieldName || undefined })
  const onSubmit = handleSubmit((values) => onSubmitAction(values))
  const transitionFieldDirty = transitionFieldName ? Boolean(dirtyFields?.[transitionFieldName]) : true
  const isSubmitDisabled = isLoading || !formState.isValid || (transitionFieldName ? !transitionFieldDirty : false)

  return (
    <DialogActions>
      <Button onClick={onClose} disabled={isLoading} variant="outlined">
        {translate('ra.action.cancel')}
      </Button>
      <Button color={confirmColor} variant={confirmVariant} onClick={onSubmit} disabled={isSubmitDisabled}>
        {translate(confirmLabelKey)}
      </Button>
    </DialogActions>
  )
}

export const IncidentReportActionDialog = ({
  open,
  onClose,
  isLoading,
  i18nPrefix,
  children,
  footerProps,
  dialogProps = {},
  formProps = {},
}) => {
  const record = useRecordContext()

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      sx={{ '& .MuiDialog-paper': { width: 'min(92vw, 755px)', maxWidth: '755px' } }}
      {...dialogProps}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <ActionModalHeader i18nPrefix={i18nPrefix} />
      </DialogTitle>
      <Form defaultValues={record} {...formProps}>
        <DialogContent sx={{ pb: 1 }}>{children}</DialogContent>
        <IncidentReportFormDialogFooter isLoading={isLoading} onClose={onClose} {...footerProps} />
      </Form>
    </Dialog>
  )
}

export const ActionCommentSection = ({
  source = 'comment',
  isRequired = false,
  titleKey = 'resources.incident-reports.actions.fields.comment',
  helperKey,
  placeholderKey,
  placeholder,
  titleIcon: TitleIcon = NotesOutlinedIcon,
  sx = {},
}) => {
  const translate = useTranslate()
  return (
    <ActionModalSection titleKey={titleKey} titleIcon={TitleIcon} helperKey={helperKey} sx={{ mt: 0, ...sx }}>
      <TextInput
        source={source}
        multiline
        minRows={3}
        fullWidth
        label={false}
        placeholder={placeholder || translate(placeholderKey)}
        sx={{ width: '100% !important', m: 0 }}
        validate={isRequired ? required() : undefined}
      />
    </ActionModalSection>
  )
}

export const useActionSubmit = (recordId, onSuccessClose) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const translate = useTranslate()
  const [actionRequest, setActionRequest] = useState(null)

  const [runAction, { isLoading }] = useApi(`/incident-reports/${recordId}/actions`, {
    method: 'POST',
    body: JSON.stringify(actionRequest?.body || {}),
    onSuccess: () => {
      notify(actionRequest?.successMessage)
      onSuccessClose()
      refresh()
    },
    onFailure: (error) => {
      const { code, message } = getErrorDataFromError(error)
      const notification = ERROR_MESSAGES[code]
        ? translate(ERROR_MESSAGES[code])
        : translate('mymove.errors.errorWithMessage', { message })
      notify(notification, { type: 'error' })
    },
  })

  useEffect(() => {
    if (actionRequest) {
      runAction()
    }
  }, [JSON.stringify(actionRequest)]) // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (action, data, successMessage) => {
    setActionRequest({
      body: { action, ...data },
      successMessage,
    })
  }

  return [submit, isLoading]
}

export const INCIDENT_REPORT_ACTIONS = {
  [INCIDENT_REPORT_ACTION_REJECT]: { name: 'reject', label: 'resources.incident-reports.actions.reject.label' },
  [INCIDENT_REPORT_ACTION_RESOLVE]: { name: 'resolve', label: 'resources.incident-reports.actions.resolve.label' },
}

export const INCIDENT_REPORT_ACTION_BUTTONS = [
  INCIDENT_REPORT_ACTION_CONFIRM,
  INCIDENT_REPORT_ACTION_REJECT,
  INCIDENT_REPORT_ACTION_RESOLVE,
  INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP,
  INCIDENT_REPORT_ACTION_UPDATE_SEVERITY,
]
